import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ContentManagerService } from '@app/modules/editor/services/content-manager.service';
import { SessionStorageService } from '@app/shared/services/storage';

import { BlockTypeEnum } from '@core/enums';
import { MixedObject } from '@core/interfaces/mixed-object';
import { BlockActionModel } from '@core/models';
import { Block } from '@core/models/block/block.model';
import { ApiUrlService } from '@core/services/common/api-url.service';
import { mapSpecialBlock } from '@shared/services/factories/block-factory.service';
import { CurrencyPreferenceService } from '@shared/services/currency/currency-preference.service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ApiBlockService {
  private apiUrl = this.apiUrlService.getApiUrl();

  constructor(private httpClient: HttpClient,
              private apiUrlService: ApiUrlService,
              private sessionStorageService: SessionStorageService,
              private contentManager: ContentManagerService,
              private currencyPreferenceService: CurrencyPreferenceService,
              @Inject(PLATFORM_ID) private platformId: any,
              @Inject(DOCUMENT) private document: Document) {
  }

  /**
   * @param variantId wariant wybrany w konfiguratorze ceny; backend wycenia wtedy blok tak
   *   samo, jak wycenia mail z tego samego formularza. `null` znaczy "bez wyboru" i zostawia
   *   mu wariant domyslny.
   */
  get(id: number | string, withPageUrlParam = false, model?: BlockActionModel, variantId: number | null = null): Observable<Block> {
    const paramsObj: any = {
      pageUrl: withPageUrlParam ? this.document.location.pathname : '',
    };
    if (model?.id) {
      paramsObj.modelId = model.id;
    }
    if (model?.className) {
      paramsObj.modelClass = model.className;
    }
    if (variantId !== null) {
      paramsObj.variant_id = variantId;
    }
    const url = this.currencyPreferenceService.appendCurrencyToUrlIfSwitcherExists(`${this.apiUrl}client/blocks/${id}${this.getUrlParams(paramsObj)}`);
    return this.httpClient.get<Block>(url)
      .pipe(map(mapSpecialBlock));
  }

  getByType(type: BlockTypeEnum, useLoader = false, pageUrl: string | null = null): Observable<Block> {
    let noCache = false;
    if (pageUrl?.includes('?')) {
      const [_url, params] = pageUrl.split('?');
      // pageUrl=url+
      const paramsArr = params.split('&').map(v => {
        const [key, value = ''] = v.split('=');

        return {key, value};
      });
      noCache = paramsArr.some(p => p.key === 'no-cache');
      const newParams = paramsArr.filter(p => p.key !== 'no-cache')
        .map(({key, value}) => {
          return `${key}=${value}`;
        }).join('&');
      pageUrl = _url + (newParams.length ? '?' + newParams : '');
    }
    const pageParams = [];
    if (noCache) {
      pageParams.push('no-cache');
    }
    if (pageUrl) {
      pageParams.push(`pageUrl=${pageUrl}`);
    }
    const pageUrlParam = pageParams.length ?
      '?' + pageParams.join('&')
      : '';

    let paramsEdit = '';

    // tryb edycyjny - warunek i przekazywanie dod
    if (pageUrlParam) {
      paramsEdit = 'type_edit';
    }
    const url = this.currencyPreferenceService.appendCurrencyToUrlIfSwitcherExists(`${this.apiUrl}client/blocks-type/${type}${pageUrlParam}${paramsEdit}`);
    const headers = useLoader ? {
        'x-show-loader': '1',
        'key': this.parseLocalizationBySystem(),
      }
      : {
        'key': this.parseLocalizationBySystem(),
      };
    return this.httpClient.get<Block>(url, {headers})
      .pipe(
        tap(block => {
          if (block?.id) {
            this.contentManager.saveMatrixContent(block);
            this.contentManager.getAndSaveBlockContentByBlock(block.id.toString())
              .subscribe({
                error: err => console.error('Błąd zapisu bloku:', err),
              });
          }
        }),
        map((block: Block) => mapSpecialBlock(block)),
      );
  }

  getMenu(
    noCache = false,
    options?: { clientId?: number; pageId?: number; menuBlockId?: number },
  ): Observable<Block> {
    const lang = this.parseLocalizationBySystem();
    const params = new URLSearchParams();
    params.set('lang', lang);
    if (noCache) {
      params.set('no-cache', '1');
    }
    if (options?.clientId != null) {
      params.set('client_id', String(options.clientId));
    }
    if (options?.pageId != null) {
      params.set('page_id', String(options.pageId));
    }
    if (options?.menuBlockId != null) {
      params.set('menu_block_id', String(options.menuBlockId));
    }
    const url = this.currencyPreferenceService.appendCurrencyToUrlIfSwitcherExists(`${this.apiUrl}client/block-menu?${params.toString()}`);
    return this.httpClient.get<any>(url, {
      headers: {
        key: lang,
      },
    }).pipe(
      tap(raw => this.currencyPreferenceService.setMenuShouldConvertCurrency(raw?.shouldConvertCurrency)),
      map(mapSpecialBlock),
    );
  }

  getFooter(noCache = false): Observable<Block> {
    const lang = this.parseLocalizationBySystem();
    const noCacheParam = noCache ? '&no-cache=1' : '';
    const url = this.currencyPreferenceService.appendCurrencyToUrlIfSwitcherExists(`${this.apiUrl}client/block-footer?lang=${lang}${noCacheParam}`);
    return this.httpClient.get<any>(url, {
      headers: {
        key: lang,
      },
    }).pipe(
      tap(raw => this.currencyPreferenceService.setFooterShouldConvertCurrency(raw?.shouldConvertCurrency)),
      map(mapSpecialBlock),
    );
  }

  getCookies(noCache = false): Observable<Block> {
    const lang = this.parseLocalizationBySystem();
    const noCacheParam = noCache ? '&no-cache=1' : '';
    const url = this.currencyPreferenceService.appendCurrencyToUrlIfSwitcherExists(`${this.apiUrl}client/block-cookies?lang=${lang}${noCacheParam}`);
    return this.httpClient.get<Block>(url, {
      headers: {
        key: lang,
      },
    }).pipe(map(mapSpecialBlock));
  }

  public getUrlParams(paramsObj: MixedObject): string {
    const params = [];
    for (const key of Object.keys(paramsObj)) {
      if (paramsObj.hasOwnProperty(key)) {
        params.push(`${key}=${paramsObj[key]}`);
      }
    }
    return params.length ? `?${params.join('&')}` : '';
  }

  public parseLocalizationBySystem() {
    if (!isPlatformBrowser(this.platformId)) {
      return '';
    }
    if (this.sessionStorageService.getItem('language')) {
      return this.sessionStorageService.getItem('language');
    }
    return navigator.language.split('-')[0];
  }
}
